import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, aiAPI } from '../api';
import { getTranslation } from '../i18n';
import LanguageTabs from '../components/LanguageTabs';
import SettingsWelcomeField from '../components/SettingsWelcomeField';
import TranslateFromEnButton from '../components/TranslateFromEnButton';
import LiveClientPreview from '../components/LiveClientPreview';
import { TEXT_LANGS, normalizeByLang } from '../utils/clientTexts';

const TONE_PRESETS = [
  { id: 'default',     labelKey: 'atm_maestra', descKey: 'atm_maestra_desc' },
  { id: 'warm-sand',   labelKey: 'atm_warm',    descKey: 'atm_warm_desc' },
  { id: 'soft-grey',   labelKey: 'atm_grey',    descKey: 'atm_grey_desc' },
  { id: 'dusty-blue',  labelKey: 'atm_blue',    descKey: 'atm_blue_desc' },
  { id: 'custom',      labelKey: 'atm_custom',  descKey: 'atm_custom_desc' },
];

const TONE_COLORS = {
  'default':    { page: '#faf8f5', section: '#ffffff', border: '#ece9e4', text: '#4a4540', textLight: '#8a847b', accent: '#b8a992' },
  'warm-sand':  { page: '#f5f0e8', section: '#faf7f2', border: '#e8e0d4', text: '#4a4035', textLight: '#8a7e72', accent: '#b09878' },
  'soft-grey':  { page: '#f0f0ee', section: '#f7f7f6', border: '#e2e2df', text: '#3c3c3a', textLight: '#7a7a76', accent: '#9a9a96' },
  'dusty-blue': { page: '#edf1f5', section: '#f5f7fa', border: '#dbe1e8', text: '#3a4048', textLight: '#6e7880', accent: '#8ea0b0' },
};

// Derive a full tone palette from a single hex color
function deriveCustomTone(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const mix = (c, t, w) => Math.round(c * w + t * (1 - w));
  const toHex = (r2, g2, b2) => '#' + [r2, g2, b2].map(c => c.toString(16).padStart(2, '0')).join('');
  return {
    page:      toHex(mix(r, 255, 0.08), mix(g, 255, 0.08), mix(b, 255, 0.08)),
    section:   toHex(mix(r, 255, 0.03), mix(g, 255, 0.03), mix(b, 255, 0.03)),
    border:    toHex(mix(r, 230, 0.12), mix(g, 230, 0.12), mix(b, 230, 0.12)),
    text:      toHex(mix(r, 50, 0.2), mix(g, 50, 0.2), mix(b, 50, 0.2)),
    textLight: toHex(mix(r, 120, 0.2), mix(g, 120, 0.2), mix(b, 120, 0.2)),
    accent:    toHex(mix(r, 160, 0.35), mix(g, 160, 0.35), mix(b, 160, 0.35)),
  };
}

function getToneColors(toneId, customColor) {
  if (toneId === 'custom' && customColor) return deriveCustomTone(customColor);
  return TONE_COLORS[toneId] || TONE_COLORS['default'];
}

// ── Board Settings — module-level settings for the Boards module ──
// Everything here shapes how CLIENTS experience boards: atmosphere (tones),
// client view texts, section titles, footer, and how the brand identity is
// displayed on boards. Workspace-level identity (logo file, brand name)
// lives in Workspace Settings (/settings).
export default function BoardSettings() {
  const navigate = useNavigate();
  document.title = 'Maestra - Board Settings';

  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [toast, setToast] = useState(null);

  const [workspaceLang, setWorkspaceLang] = useState('en');
  const t = getTranslation(workspaceLang);

  // Read-only identity (managed in Workspace Settings) — used by the live preview
  const [brandName, setBrandName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');

  // Board-level display settings
  const [clientTone, setClientTone] = useState('default');
  const [customColor, setCustomColor] = useState('#c4a882');
  const [brandingDisplayMode, setBrandingDisplayMode] = useState('logo_and_name');
  const [logoSize, setLogoSize] = useState('M');
  const [saving, setSaving] = useState(false);

  // Client view texts (per-language) + shared footer fields
  const [welcomeByLang, setWelcomeByLang] = useState({
    en: {}, pl: {}, de: {},
  });
  const [editLang, setEditLang] = useState('en');
  const [templateTexts, setTemplateTexts] = useState({});
  const [footerTexts, setFooterTexts] = useState({
    custom_footer_text: '',
    custom_footer_subtext: '',
    custom_footer_link_label: '',
    custom_footer_link_url: '',
  });

  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const toastTimerRef = React.useRef(null);

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [settingsRes, brandingRes] = await Promise.all([
        authAPI.getSettings(),
        authAPI.getBranding(),
      ]);
      setSettings(settingsRes.data);

      const b = brandingRes.data;
      setBrandName(b.overrides?.workspace_name || b.resolved?.workspace_name || '');
      const savedTone = b.overrides?.client_tone || b.resolved?.client_tone || 'default';
      setClientTone(savedTone === 'ivory' ? 'default' : savedTone);
      setCustomColor(b.overrides?.custom_color || b.resolved?.custom_color || '#c4a882');
      setLogoUrl(b.resolved?.workspace_logo_url || '');
      setBrandingDisplayMode(b.overrides?.branding_display_mode ?? b.resolved?.branding_display_mode ?? 'logo_and_name');
      setLogoSize(b.overrides?.logo_size ?? b.resolved?.logo_size ?? 'M');

      const texts = b.overrides?.texts || b.resolved?.texts || {};
      const normalized = normalizeByLang(texts);
      const tplNorm = normalizeByLang(b.template_texts || {});
      setTemplateTexts(tplNorm);

      const I18N_BASE = {
        welcome_title: 'welcome',
        welcome_subtitle: 'client_intro',
        welcome_description: 'client_hint',
        instruction_text: 'instruction_main',
        instruction_react: 'instruction_react',
        inspiration_title: 'your_inspirations',
        inspiration_description: 'inspiration_intro',
        notes_title: 'notes_title',
        notes_description: 'notes_subtitle',
        documents_title: 'documents_title',
        documents_description: 'documents_subtitle',
      };
      const defOf = (lang, key) => {
        const ld = tplNorm?.[lang] || {};
        if (ld[key]) return ld[key];
        const en = tplNorm?.en || {};
        if (en[key]) return en[key];
        const baseKey = I18N_BASE[key];
        if (baseKey) {
          const tForLang = getTranslation(lang) || {};
          if (tForLang[baseKey]) return tForLang[baseKey];
        }
        return '';
      };
      const overrideOr = (lang, key) => {
        const v = (normalized[lang] || {})[key];
        return v === undefined || v === null ? defOf(lang, key) : v;
      };

      const seeded = {};
      for (const lang of TEXT_LANGS) {
        const langTexts = normalized[lang] || {};
        seeded[lang] = {
          welcome_title:           overrideOr(lang, 'welcome_title'),
          welcome_subtitle:        overrideOr(lang, 'welcome_subtitle'),
          welcome_description:     overrideOr(lang, 'welcome_description'),
          custom_message:          langTexts.custom_message ?? '',
          instruction_text:        overrideOr(lang, 'instruction_text'),
          instruction_react:       overrideOr(lang, 'instruction_react'),
          inspiration_title:       overrideOr(lang, 'inspiration_title'),
          inspiration_description: overrideOr(lang, 'inspiration_description'),
          notes_title:             overrideOr(lang, 'notes_title'),
          notes_description:       overrideOr(lang, 'notes_description'),
          documents_title:         overrideOr(lang, 'documents_title'),
          documents_description:   overrideOr(lang, 'documents_description'),
        };
      }
      setWelcomeByLang(seeded);
      setFooterTexts({
        custom_footer_text: normalized.custom_footer_text ?? '',
        custom_footer_subtext: normalized.custom_footer_subtext ?? '',
        custom_footer_link_label: normalized.custom_footer_link_label ?? '',
        custom_footer_link_url: normalized.custom_footer_link_url ?? '',
      });

      setWorkspaceLang(settingsRes.data.language || 'en');
      setEditLang(settingsRes.data.language || 'en');
    } catch (err) {
      console.error('Error loading board settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (msg) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(msg);
    toastTimerRef.current = setTimeout(() => setToast(null), 3000);
  };

  // ---- Text default helpers (same UX as before the settings split) ----
  const I18N_BASE_FOR_WELCOME = {
    welcome_title: 'welcome',
    welcome_subtitle: 'client_intro',
    welcome_description: 'client_hint',
    instruction_text: 'instruction_main',
    instruction_react: 'instruction_react',
    inspiration_title: 'your_inspirations',
    inspiration_description: 'inspiration_intro',
    notes_title: 'notes_title',
    notes_description: 'notes_subtitle',
    documents_title: 'documents_title',
    documents_description: 'documents_subtitle',
  };
  const getDefaultText = (key) => {
    const langDefaults = templateTexts?.[editLang] || {};
    if (langDefaults[key]) return langDefaults[key];
    const enDefaults = templateTexts?.en || {};
    if (enDefaults[key]) return enDefaults[key];
    const baseKey = I18N_BASE_FOR_WELCOME[key];
    if (baseKey) {
      const tForLang = getTranslation(editLang) || {};
      if (tForLang[baseKey]) return tForLang[baseKey];
    }
    return '';
  };
  const isFieldCustom = (key) => {
    const v = ((welcomeByLang[editLang] || {})[key] || '').trim();
    if (!v) return false;
    return v !== (getDefaultText(key) || '').trim();
  };
  const updateWelcomeField = (key, value) => {
    setWelcomeByLang(prev => ({
      ...prev,
      [editLang]: { ...(prev[editLang] || {}), [key]: value },
    }));
  };
  const resetWelcomeFieldToDefault = (key) => {
    const def = getDefaultText(key) || '';
    updateWelcomeField(key, def);
  };
  const resetAllWelcomeTextsToDefault = () => {
    const fresh = {};
    for (const lang of TEXT_LANGS) {
      const prev = welcomeByLang[lang] || {};
      const ld = templateTexts?.[lang] || {};
      const en = templateTexts?.en || {};
      const tForLang = getTranslation(lang) || {};
      fresh[lang] = {
        welcome_title:           ld.welcome_title           || en.welcome_title           || tForLang.welcome              || '',
        welcome_subtitle:        ld.welcome_subtitle        || en.welcome_subtitle        || tForLang.client_intro         || '',
        welcome_description:     ld.welcome_description     || en.welcome_description     || tForLang.client_hint          || '',
        custom_message:          prev.custom_message        || '',
        instruction_text:        ld.instruction_text        || en.instruction_text        || tForLang.instruction_main     || '',
        instruction_react:       ld.instruction_react       || en.instruction_react       || tForLang.instruction_react    || '',
        inspiration_title:       ld.inspiration_title       || en.inspiration_title       || tForLang.your_inspirations    || '',
        inspiration_description: ld.inspiration_description || en.inspiration_description || tForLang.inspiration_intro    || '',
        notes_title:             ld.notes_title             || en.notes_title             || tForLang.notes_title           || '',
        notes_description:       ld.notes_description       || en.notes_description       || tForLang.notes_subtitle        || '',
        documents_title:         ld.documents_title         || en.documents_title         || tForLang.documents_title       || '',
        documents_description:   ld.documents_description   || en.documents_description   || tForLang.documents_subtitle    || '',
      };
    }
    setWelcomeByLang(fresh);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const enOverride = welcomeByLang.en || {};
      const enTemplate = templateTexts?.en || {};

      const defAt = (lang2, key) => {
        const ld = templateTexts?.[lang2] || {};
        if (ld[key]) return ld[key];
        const en2 = templateTexts?.en || {};
        return en2[key] || '';
      };

      // Auto-translate untouched PL/DE buckets from customized EN values
      const autoTranslated = {};
      for (const lang of TEXT_LANGS) {
        if (lang === 'en') continue;
        const userBucket = welcomeByLang[lang] || {};
        const missing = {};
        for (const [k, v] of Object.entries(enOverride)) {
          const enVal = (v || '').trim();
          if (!enVal) continue;
          if (enVal === (enTemplate[k] || '').trim()) continue;
          const targetVal = userBucket[k];
          if (targetVal !== undefined && targetVal !== defAt(lang, k)) continue;
          missing[k] = enVal;
        }
        if (Object.keys(missing).length === 0) continue;
        try {
          showToast(`Translating to ${lang.toUpperCase()}…`);
          const { data } = await aiAPI.translateTexts('en', lang, missing);
          const translations = data?.translations || {};
          if (Object.keys(translations).length > 0) {
            autoTranslated[lang] = translations;
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn(`Auto-translate to ${lang} failed`, err);
        }
      }

      if (Object.keys(autoTranslated).length > 0) {
        setWelcomeByLang(prev => {
          const next = { ...prev };
          for (const [lang, vals] of Object.entries(autoTranslated)) {
            next[lang] = { ...(prev[lang] || {}), ...vals };
          }
          return next;
        });
      }

      // Build payload: skip values matching template defaults; keep
      // intentional clears ("") and custom strings.
      const textsToSave = {};
      for (const lang of TEXT_LANGS) {
        const userBucket = welcomeByLang[lang] || {};
        const aiBucket = autoTranslated[lang] || {};
        const merged = { ...userBucket, ...aiBucket };
        const clean = {};
        for (const [k, vRaw] of Object.entries(merged)) {
          const v = vRaw == null ? '' : String(vRaw);
          if (k === 'custom_message') {
            if (v.trim()) clean[k] = v.trim();
            continue;
          }
          const def = defAt(lang, k);
          if (v === def) continue;
          clean[k] = v;
        }
        textsToSave[lang] = clean;
      }
      Object.entries(footerTexts).forEach(([k, v]) => {
        textsToSave[k] = v == null ? '' : String(v);
      });

      await authAPI.updateBranding({
        client_tone: clientTone,
        custom_color: clientTone === 'custom' ? customColor : '',
        show_logo_in_client: brandingDisplayMode !== 'name_only',
        branding_display_mode: brandingDisplayMode,
        logo_size: logoSize,
        texts: textsToSave,
      });
      const filledLangs = Object.keys(autoTranslated);
      if (filledLangs.length > 0) {
        showToast(`${t.branding_saved} · auto-filled ${filledLangs.map(l => l.toUpperCase()).join(' & ')}`);
      } else {
        showToast(t.branding_saved);
      }
    } catch (err) {
      showToast(err.response?.data?.detail || 'Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const canBrand = settings?.can_customize_branding;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '120px' }}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="fade-in user-settings-wrap" data-testid="board-settings-page">
      {/* Header */}
      <div className="settings-branding-section" style={{ marginBottom: '36px' }}>
        <button
          onClick={() => navigate('/boards')}
          data-testid="board-settings-back-btn"
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: '13px', color: 'var(--kahla-text-light)',
            padding: 0, marginBottom: '16px', fontFamily: 'inherit',
          }}
        >
          ← {t.boards_settings_back || 'Boards'}
        </button>
        <p className="branding-section-eyebrow">{t.branding_eyebrow}</p>
        <h1 style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: '28px',
          color: 'var(--kahla-charcoal)',
          margin: 0,
        }}>
          {t.boards_settings_title || 'Board Settings'}
        </h1>
        <p style={{
          fontFamily: 'Cormorant Garamond, Playfair Display, serif',
          fontStyle: 'italic', fontSize: '15px',
          color: 'var(--kahla-text-light)', margin: '10px 0 0', lineHeight: 1.7, maxWidth: 560,
        }}>
          {t.boards_settings_subtitle}
        </p>
      </div>

      {!canBrand ? (
        <div
          className="settings-branding-section"
          data-testid="board-settings-locked"
          style={{
            background: 'var(--kahla-white)', border: '1px solid var(--kahla-border)',
            padding: '32px 36px',
          }}
        >
          <p style={{
            fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic',
            fontSize: '15px', color: 'var(--kahla-text-light)', margin: 0, lineHeight: 1.7,
          }}>
            {t.boards_settings_locked || 'Board customization is not available on your current plan.'}
          </p>
        </div>
      ) : (
        <div className="settings-branding-section" data-testid="board-settings-content">
          <div className="branding-grid">
            <div className="branding-edit">

              {/* ─── CARD 1: Display in Client View ─── */}
              <div className="branding-card" data-testid="card-client-display">
                <div className="branding-card__head">
                  <h3 className="branding-card__title">{t.display_in_client_view}</h3>
                  <p className="branding-card__subtitle">{t.display_in_client_help}</p>
                </div>
                <div className="branding-card__body">
                  <div>
                    <select
                      data-testid="settings-display-mode"
                      value={brandingDisplayMode}
                      onChange={e => setBrandingDisplayMode(e.target.value)}
                      style={{
                        padding: '8px 12px', fontSize: '13px', border: '1px solid var(--kahla-border)',
                        fontFamily: 'inherit', background: 'var(--kahla-white)', cursor: 'pointer',
                        width: '100%', boxSizing: 'border-box',
                      }}
                    >
                      <option value="logo_and_name">{t.logo_and_name}</option>
                      <option value="logo_only">{t.logo_only}</option>
                      <option value="name_only">{t.brand_name_only}</option>
                    </select>
                  </div>

                  {brandingDisplayMode !== 'name_only' && (
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--kahla-text-light)', marginBottom: '6px' }}>
                        {t.logo_size_label}
                      </label>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        {['S', 'M', 'L'].map(size => (
                          <button
                            key={size}
                            data-testid={`logo-size-${size}`}
                            onClick={() => setLogoSize(size)}
                            style={{
                              padding: '6px 16px', fontSize: '12px', fontWeight: logoSize === size ? '600' : '400',
                              border: '1px solid var(--kahla-border)',
                              background: logoSize === size ? 'var(--kahla-text)' : 'transparent',
                              color: logoSize === size ? 'var(--kahla-white)' : 'var(--kahla-text-light)',
                              cursor: 'pointer', fontFamily: 'inherit',
                            }}
                          >{size}</button>
                        ))}
                      </div>
                    </div>
                  )}

                  <p style={{ fontSize: '11px', color: 'var(--kahla-text-light)', margin: 0, fontStyle: 'italic', lineHeight: 1.6 }}>
                    {t.workspace_logo_managed_hint}{' '}
                    <button
                      type="button"
                      data-testid="board-settings-open-workspace-link"
                      onClick={() => navigate('/settings')}
                      style={{
                        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                        fontFamily: 'inherit', fontSize: '11px', color: 'var(--kahla-charcoal)',
                        textDecoration: 'underline', textUnderlineOffset: '3px', fontStyle: 'italic',
                      }}
                    >
                      {t.open_workspace_settings || 'Workspace Settings'}
                    </button>
                  </p>
                </div>
              </div>

              {/* ─── CARD 2: Client Experience (atmosphere) ─── */}
              <div className="branding-card" data-testid="card-client-experience">
                <div className="branding-card__head">
                  <h3 className="branding-card__title">{t.card_client_experience}</h3>
                  <p className="branding-card__subtitle">{t.card_client_experience_desc}</p>
                </div>
                <div className="branding-card__body">
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px' }}>
                    {TONE_PRESETS.map(tone => {
                      const isSelected = clientTone === tone.id;
                      const colors = tone.id === 'custom'
                        ? deriveCustomTone(customColor)
                        : (TONE_COLORS[tone.id] || TONE_COLORS['default']);
                      return (
                        <button
                          key={tone.id}
                          data-testid={`tone-${tone.id}`}
                          onClick={() => setClientTone(tone.id)}
                          style={{
                            display: 'flex', flexDirection: 'column', padding: 0, border: 'none',
                            cursor: 'pointer', fontFamily: 'inherit', overflow: 'hidden', borderRadius: '3px',
                            boxShadow: isSelected
                              ? '0 0 0 2px var(--kahla-text), 0 2px 8px rgba(0,0,0,0.08)'
                              : '0 0 0 1px var(--kahla-border)',
                            transition: 'box-shadow 0.2s ease', background: 'transparent',
                          }}
                        >
                          <div style={{
                            height: '56px', background: colors.page, position: 'relative',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '3px',
                          }}>
                            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '16px',
                              background: `linear-gradient(180deg, ${colors.section} 0%, ${colors.page} 100%)` }} />
                            <div style={{ width: '32px', height: '3px', background: colors.text, opacity: 0.85, borderRadius: '1px', zIndex: 1 }} />
                            <div style={{ display: 'flex', gap: '3px', zIndex: 1 }}>
                              {[1,2,3].map(k => (
                                <div key={k} style={{ width: '10px', height: '10px', background: colors.section, border: `1px solid ${colors.border}`, borderRadius: '1px' }} />
                              ))}
                            </div>
                          </div>
                          <div style={{ padding: '10px 8px', textAlign: 'center', background: isSelected ? 'var(--kahla-text)' : 'transparent', color: isSelected ? 'var(--kahla-white)' : 'var(--kahla-text)' }}>
                            <p style={{ fontSize: '12px', fontWeight: '500', margin: 0 }}>{t[tone.labelKey] || tone.labelKey}</p>
                            <p style={{ fontSize: '10px', opacity: 0.7, margin: '2px 0 0' }}>{t[tone.descKey] || ''}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {clientTone === 'custom' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', border: '1px solid var(--kahla-border)', background: 'var(--kahla-cream-light, #faf8f5)' }}>
                      <input
                        type="color"
                        value={customColor}
                        onChange={e => setCustomColor(e.target.value)}
                        data-testid="custom-color-picker"
                        style={{ width: '40px', height: '40px', border: '1px solid var(--kahla-border)', borderRadius: '2px', cursor: 'pointer', padding: 0, flexShrink: 0 }}
                      />
                      <div>
                        <span style={{ fontSize: '12px', color: 'var(--kahla-text)', fontFamily: 'monospace' }}>{customColor}</span>
                        <p style={{ fontSize: '11px', color: 'var(--kahla-text-light)', margin: '2px 0 0', fontStyle: 'italic' }}>
                          {t.custom_color_helper || "We'll derive a soft palette from this color."}
                        </p>
                      </div>
                    </div>
                  )}

                  {clientTone !== 'default' && (
                    <button
                      data-testid="restore-default-tone"
                      onClick={() => { setClientTone('default'); setCustomColor('#c4a882'); }}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px',
                        color: 'var(--kahla-text-light)', fontFamily: 'inherit',
                        textDecoration: 'underline', textUnderlineOffset: '3px', padding: 0,
                        alignSelf: 'flex-start',
                      }}
                    >
                      {t.atm_maestra}
                    </button>
                  )}
                </div>
              </div>

              {/* ─── CARD 3: Client View Texts ─── */}
              <div className="branding-card" data-testid="card-client-view-texts">
                <div className="branding-card__head">
                  <h3 className="branding-card__title">{t.card_client_view_texts}</h3>
                  <p className="branding-card__subtitle">{t.client_view_texts_desc}</p>
                </div>
                <div className="branding-card__body">
                  <LanguageTabs value={editLang} onChange={setEditLang} testId="settings-lang-tabs" />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                    <p style={{ fontSize: '11px', color: 'var(--kahla-text-light)', fontStyle: 'italic', margin: 0 }}>
                      {t.editing_overrides_for} <strong style={{ color: 'var(--kahla-charcoal)' }}>{editLang.toUpperCase()}</strong>.
                    </p>
                    <button
                      type="button"
                      data-testid="settings-reset-all-texts-btn"
                      onClick={resetAllWelcomeTextsToDefault}
                      style={{
                        background: 'none', border: 'none', padding: 0, fontSize: '11px',
                        color: 'var(--kahla-taupe)', cursor: 'pointer', fontFamily: 'inherit',
                        textDecoration: 'underline', textDecorationColor: 'rgba(166,152,136,0.4)',
                        textUnderlineOffset: '3px', whiteSpace: 'nowrap', transition: 'color 0.2s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--kahla-charcoal)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--kahla-taupe)'; }}
                      title={t.reset_all_welcome_texts_tooltip}
                    >
                      {t.reset_all_to_default}
                    </button>
                  </div>
                  <TranslateFromEnButton
                    targetLang={editLang}
                    testId="settings-translate-btn"
                    t={t}
                    getSourceTexts={() => {
                      const enOverride = welcomeByLang.en || {};
                      const enTemplate = templateTexts?.en || {};
                      const keys = new Set([
                        ...Object.keys(enOverride),
                        ...Object.keys(enTemplate),
                      ]);
                      const merged = {};
                      for (const k of keys) {
                        const overrideVal = (enOverride[k] || '').trim();
                        merged[k] = overrideVal || (enTemplate[k] || '');
                      }
                      return merged;
                    }}
                    getTargetTexts={() => welcomeByLang[editLang] || {}}
                    onTranslated={(translations) => {
                      setWelcomeByLang(prev => ({
                        ...prev,
                        [editLang]: { ...(prev[editLang] || {}), ...translations },
                      }));
                    }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <SettingsWelcomeField
                      label={t.welcome_title_label}
                      testId="settings-welcome-title"
                      value={welcomeByLang[editLang]?.welcome_title || ''}
                      onChange={(v) => updateWelcomeField('welcome_title', v)}
                      defaultValue={getDefaultText('welcome_title')}
                      isCustom={isFieldCustom('welcome_title')}
                      onUseDefault={() => resetWelcomeFieldToDefault('welcome_title')}
                      t={t}
                    />
                    <SettingsWelcomeField
                      label={t.description_text_label}
                      testId="settings-description-text"
                      value={welcomeByLang[editLang]?.welcome_subtitle || ''}
                      onChange={(v) => updateWelcomeField('welcome_subtitle', v)}
                      defaultValue={getDefaultText('welcome_subtitle')}
                      isCustom={isFieldCustom('welcome_subtitle')}
                      onUseDefault={() => resetWelcomeFieldToDefault('welcome_subtitle')}
                      multiline
                      t={t}
                    />
                    <SettingsWelcomeField
                      label={t.welcome_hint_label}
                      testId="settings-welcome-description"
                      value={welcomeByLang[editLang]?.welcome_description || ''}
                      onChange={(v) => updateWelcomeField('welcome_description', v)}
                      defaultValue={getDefaultText('welcome_description')}
                      isCustom={isFieldCustom('welcome_description')}
                      onUseDefault={() => resetWelcomeFieldToDefault('welcome_description')}
                      t={t}
                    />
                    <SettingsWelcomeField
                      label={t.custom_message_label}
                      testId="settings-custom-message"
                      value={welcomeByLang[editLang]?.custom_message || ''}
                      onChange={(v) => updateWelcomeField('custom_message', v)}
                      defaultValue={getDefaultText('custom_message')}
                      isCustom={isFieldCustom('custom_message')}
                      onUseDefault={() => resetWelcomeFieldToDefault('custom_message')}
                      multiline
                      t={t}
                    />
                  </div>
                </div>
              </div>

              {/* ─── CARD 4b: Inspirations Section ─── */}
              <div className="branding-card" data-testid="card-inspirations-section">
                <div className="branding-card__head">
                  <h3 className="branding-card__title">{t.card_inspirations_section}</h3>
                  <p className="branding-card__subtitle">{t.inspirations_section_desc}</p>
                </div>
                <div className="branding-card__body">
                  <SettingsWelcomeField
                    label={t.inspirations_title_label}
                    testId="settings-inspiration-title"
                    value={welcomeByLang[editLang]?.inspiration_title || ''}
                    onChange={(v) => updateWelcomeField('inspiration_title', v)}
                    defaultValue={getDefaultText('inspiration_title')}
                    isCustom={isFieldCustom('inspiration_title')}
                    onUseDefault={() => resetWelcomeFieldToDefault('inspiration_title')}
                    t={t}
                  />
                  <SettingsWelcomeField
                    label={t.inspirations_description_label}
                    testId="settings-inspiration-description"
                    value={welcomeByLang[editLang]?.inspiration_description || ''}
                    onChange={(v) => updateWelcomeField('inspiration_description', v)}
                    defaultValue={getDefaultText('inspiration_description')}
                    isCustom={isFieldCustom('inspiration_description')}
                    onUseDefault={() => resetWelcomeFieldToDefault('inspiration_description')}
                    multiline
                    t={t}
                  />
                </div>
              </div>

              {/* ─── CARD 4c: Notes Section ─── */}
              <div className="branding-card" data-testid="card-notes-section">
                <div className="branding-card__head">
                  <h3 className="branding-card__title">{t.card_notes_section}</h3>
                  <p className="branding-card__subtitle">{t.notes_section_desc}</p>
                </div>
                <div className="branding-card__body">
                  <SettingsWelcomeField
                    label={t.notes_title_label}
                    testId="settings-notes-title"
                    value={welcomeByLang[editLang]?.notes_title || ''}
                    onChange={(v) => updateWelcomeField('notes_title', v)}
                    defaultValue={getDefaultText('notes_title')}
                    isCustom={isFieldCustom('notes_title')}
                    onUseDefault={() => resetWelcomeFieldToDefault('notes_title')}
                    t={t}
                  />
                  <SettingsWelcomeField
                    label={t.notes_description_label}
                    testId="settings-notes-description"
                    value={welcomeByLang[editLang]?.notes_description || ''}
                    onChange={(v) => updateWelcomeField('notes_description', v)}
                    defaultValue={getDefaultText('notes_description')}
                    isCustom={isFieldCustom('notes_description')}
                    onUseDefault={() => resetWelcomeFieldToDefault('notes_description')}
                    t={t}
                  />
                </div>
              </div>

              {/* ─── CARD 4d: Documents Section ─── */}
              <div className="branding-card" data-testid="card-documents-section">
                <div className="branding-card__head">
                  <h3 className="branding-card__title">{t.card_documents_section}</h3>
                  <p className="branding-card__subtitle">{t.documents_section_desc}</p>
                </div>
                <div className="branding-card__body">
                  <SettingsWelcomeField
                    label={t.documents_title_label}
                    testId="settings-documents-title"
                    value={welcomeByLang[editLang]?.documents_title || ''}
                    onChange={(v) => updateWelcomeField('documents_title', v)}
                    defaultValue={getDefaultText('documents_title')}
                    isCustom={isFieldCustom('documents_title')}
                    onUseDefault={() => resetWelcomeFieldToDefault('documents_title')}
                    t={t}
                  />
                  <SettingsWelcomeField
                    label={t.documents_description_label}
                    testId="settings-documents-description"
                    value={welcomeByLang[editLang]?.documents_description || ''}
                    onChange={(v) => updateWelcomeField('documents_description', v)}
                    defaultValue={getDefaultText('documents_description')}
                    isCustom={isFieldCustom('documents_description')}
                    onUseDefault={() => resetWelcomeFieldToDefault('documents_description')}
                    t={t}
                  />
                </div>
              </div>

              {/* ─── CARD 5: Footer ─── */}
              <div className="branding-card" data-testid="card-footer">
                <div className="branding-card__head">
                  <h3 className="branding-card__title">{t.card_footer}</h3>
                  <p className="branding-card__subtitle">{t.client_view_footer_desc}</p>
                </div>
                <div className="branding-card__body">
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--kahla-text-light)', marginBottom: '6px' }}>
                      {t.footer_text_label}
                    </label>
                    <input
                      value={footerTexts.custom_footer_text}
                      onChange={(e) => setFooterTexts(prev => ({ ...prev, custom_footer_text: e.target.value }))}
                      placeholder={brandName || t.your_brand_name_placeholder || (t.brand_name_label || 'Your brand name')}
                      data-testid="settings-custom-footer-text"
                      style={{
                        padding: '10px 14px', fontSize: '14px', width: '100%', boxSizing: 'border-box',
                        border: '1px solid var(--kahla-border)', fontFamily: 'inherit',
                      }}
                    />
                  </div>

                  <div className="branding-advanced-inline" data-testid="footer-advanced-section">
                    <div className="branding-advanced__body">
                      <div>
                        <label style={{ display: 'block', fontSize: '11px', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--kahla-text-light)', marginBottom: '6px' }}>
                          {t.footer_subtext_label} <span style={{ fontStyle: 'italic', opacity: 0.7, textTransform: 'none', letterSpacing: 'normal' }}>({t.optional})</span>
                        </label>
                        <input
                          value={footerTexts.custom_footer_subtext}
                          onChange={(e) => setFooterTexts(prev => ({ ...prev, custom_footer_subtext: e.target.value }))}
                          placeholder=""
                          data-testid="settings-custom-footer-subtext"
                          style={{
                            padding: '10px 14px', fontSize: '14px', width: '100%', boxSizing: 'border-box',
                            border: '1px solid var(--kahla-border)', fontFamily: 'inherit',
                          }}
                        />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '11px', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--kahla-text-light)', marginBottom: '6px' }}>
                            {t.link_label_label} <span style={{ fontStyle: 'italic', opacity: 0.7, textTransform: 'none', letterSpacing: 'normal' }}>({t.optional})</span>
                          </label>
                          <input
                            value={footerTexts.custom_footer_link_label}
                            onChange={(e) => setFooterTexts(prev => ({ ...prev, custom_footer_link_label: e.target.value }))}
                            placeholder=""
                            data-testid="settings-custom-footer-link-label"
                            style={{
                              padding: '10px 14px', fontSize: '14px', width: '100%', boxSizing: 'border-box',
                              border: '1px solid var(--kahla-border)', fontFamily: 'inherit',
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '11px', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--kahla-text-light)', marginBottom: '6px' }}>
                            {t.link_url_label} <span style={{ fontStyle: 'italic', opacity: 0.7, textTransform: 'none', letterSpacing: 'normal' }}>({t.optional})</span>
                          </label>
                          <input
                            value={footerTexts.custom_footer_link_url}
                            onChange={(e) => setFooterTexts(prev => ({ ...prev, custom_footer_link_url: e.target.value }))}
                            placeholder="https://..."
                            data-testid="settings-custom-footer-link-url"
                            style={{
                              padding: '10px 14px', fontSize: '14px', width: '100%', boxSizing: 'border-box',
                              border: '1px solid var(--kahla-border)', fontFamily: 'inherit',
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Save */}
              <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn-primary"
                  data-testid="settings-save-branding-btn"
                  style={{ padding: '10px 28px', fontSize: '13px' }}
                >
                  {saving ? t.saving_short : (t.save_branding || 'Save')}
                </button>
              </div>

            </div>{/* /.branding-edit */}

            <div className="branding-preview-col">
              <div className="branding-preview-sticky">
                <p className="branding-preview-label" data-testid="branding-preview-label">
                  <span className="branding-preview-pulse" aria-hidden="true" />
                  {t.client_view_preview}
                </p>
                <LiveClientPreview
                  colors={getToneColors(clientTone, customColor)}
                  brandName={brandName}
                  logoUrl={logoUrl}
                  brandingDisplayMode={brandingDisplayMode}
                  logoSize={logoSize}
                  welcomeByLang={welcomeByLang}
                  templateTexts={templateTexts}
                  footerTexts={footerTexts}
                  editLang={editLang}
                  t={t}
                />
              </div>
            </div>{/* /.branding-preview-col */}
          </div>{/* /.branding-grid */}

          {/* Mobile floating "Live preview" button → opens bottom-sheet modal */}
          <button
            type="button"
            className="mobile-preview-fab"
            data-testid="mobile-preview-fab"
            onClick={() => setShowMobilePreview(true)}
          >
            <span className="mobile-preview-fab__dot" aria-hidden="true" />
            {t.preview_open_btn}
          </button>

          {showMobilePreview && (
            <div
              className="mobile-preview-sheet"
              data-testid="mobile-preview-sheet"
              onClick={(e) => { if (e.target === e.currentTarget) setShowMobilePreview(false); }}
            >
              <div className="mobile-preview-sheet__panel">
                <div className="mobile-preview-sheet__head">
                  <p className="mobile-preview-sheet__title">
                    <span className="branding-preview-pulse" aria-hidden="true" />
                    {t.client_view_preview}
                  </p>
                  <button
                    type="button"
                    className="mobile-preview-sheet__close"
                    aria-label={t.preview_close_btn}
                    onClick={() => setShowMobilePreview(false)}
                    data-testid="mobile-preview-close"
                  >×</button>
                </div>
                <LiveClientPreview
                  colors={getToneColors(clientTone, customColor)}
                  brandName={brandName}
                  logoUrl={logoUrl}
                  brandingDisplayMode={brandingDisplayMode}
                  logoSize={logoSize}
                  welcomeByLang={welcomeByLang}
                  templateTexts={templateTexts}
                  footerTexts={footerTexts}
                  editLang={editLang}
                  t={t}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
